import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  Package, Calendar, Tag, CheckCircle2, Server, FileText, ArrowRight, ExternalLink, Puzzle, Info,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import ServiceHero, { Highlight } from "./ServiceHero";
import IncludesExcludes from "./IncludesExcludes";
import ProductCard from "./ProductCard";

/* scroll animation */
function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setIsVisible(true); obs.unobserve(el); } }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, isVisible };
}
function AnimateIn({ children, className = "", delay = 0 }: { children: ReactNode; className?: string; delay?: number }) {
  const { ref, isVisible } = useInView();
  return (
    <div ref={ref} className={className} style={{
      opacity: isVisible ? 1 : 0,
      transform: isVisible ? "translateY(0)" : "translateY(30px)",
      transition: `opacity 0.7s cubic-bezier(0.16,1,0.3,1) ${delay}s, transform 0.7s cubic-bezier(0.16,1,0.3,1) ${delay}s`,
    }}>{children}</div>
  );
}

const needsList = [
  "Logo y manual de marca (si lo tienes)",
  "Banco de imágenes y vídeos de tu negocio",
  "Referencias visuales (webs que te gusten)",
  "Información de tu negocio, servicios y objetivos",
  "Dominio deseado (lo registramos nosotros el primer año)",
];

const steps = [
  { n: 1, title: "Contratas el servicio", desc: "Pago único de 1.500€ (o 3 plazos con Klarna)." },
  { n: 2, title: "Nos das tu material", desc: "Logo, imágenes, info y referencias." },
  { n: 3, title: "Diseñamos tu web", desc: "Maquetación, redacción y configuración técnica." },
  { n: 4, title: "Revisas y ajustamos", desc: "Hasta 2 rondas de revisión." },
  { n: 5, title: "Publicamos", desc: "Tu web online con hosting y dominio incluidos." },
];

const WebCorporativaDemo = () => {
  return (
    <div className="space-y-28 overflow-hidden">
      {/* HERO */}
      <ServiceHero
        eyebrow="Servicio puntual · Pago único"
        title={<>Web <Highlight>Corporativa</Highlight></>}
        description={<>Una web profesional que representa tu marca y convierte visitas en clientes. Diseño a medida, textos incluidos y todo listo para funcionar.</>}
        includes={["1.500€ (IVA incl.) · Pago único", "Financiable en 3 plazos con Klarna", "Entrega en 20-40 días"]}
        primaryCta={{ label: "Solicitar mi web", href: "/#contact" }}
        secondaryCta={{ label: "Ver qué incluye", href: "#incluye" }}
      />

      {/* INCLUYE / NO INCLUYE — JUSTO DEBAJO DEL HERO */}
      <div id="incluye">
        <IncludesExcludes
          includes={[
            "Web de hasta <strong>8 páginas completas</strong>",
            "Redacción de textos incluida",
            "<strong>Multilingüe sin máximo de idiomas</strong>",
            "Hosting + dominio incluidos el primer año (después 149€/año)",
            "Páginas legales (aviso legal, privacidad, cookies)",
            "Formularios de contacto",
            "Integración de widgets externos que YA tengas, vía iframe (Calendly, Cal.com, TheFork...)",
            "Asesoramiento profesional sobre qué herramientas usar (reservas, pasarelas...)",
            "Configuración SEO básica on-page",
            "Maquetación responsive (móvil, tablet, escritorio)",
            "Analytics 4 + Search Console",
            "Optimización de velocidad",
            "Hasta 2 rondas de revisión",
          ]}
          excludes={[
            "Configuración de herramientas nuevas (te asesoramos cuál usar y cómo; tú la creas y configuras, nosotros integramos el iframe)",
            "Tienda online / e-commerce (es el servicio de Tienda Online)",
            "Funcionalidades a medida (áreas de cliente, integraciones complejas)",
            "Más de 8 páginas (presupuesto adicional)",
            "Formación de edición de la web",
            "Mantenimiento de cambios posteriores (servicio mensual de Mantenimiento Web)",
          ]}
        />
      </div>

      {/* WIDGETS Y HERRAMIENTAS */}
      <section>
        <AnimateIn>
          <div className="max-w-4xl mx-auto rounded-3xl p-8 md:p-12 bg-gradient-to-br from-primary/10 via-accent/5 to-primary/10 border-2 border-primary/30 shadow-glow">
            <div className="flex items-start gap-5">
              <div className="w-14 h-14 rounded-2xl bg-primary text-primary-foreground flex items-center justify-center shrink-0">
                <Puzzle className="w-7 h-7" />
              </div>
              <div>
                <Badge variant="secondary" className="mb-3">Widgets y herramientas</Badge>
                <h3 className="text-2xl md:text-3xl font-bold mb-3">¿Necesitas reservas, pagos o agenda? Te asesoramos</h3>
                <p className="text-muted-foreground">
                  Si quieres un sistema de reservas, una pasarela o cualquier herramienta externa, te indicamos profesionalmente cuál es la mejor para tu caso y cómo montarla. <strong className="text-foreground">Tú la creas y configuras</strong> siguiendo nuestras indicaciones (por ejemplo: crear tu evento en Cal.com), nos pasas el iframe y nosotros lo integramos perfectamente en tu web. Si ya la tienes funcionando, la integramos directamente.
                </p>
              </div>
            </div>
          </div>
        </AnimateIn>
      </section>

      {/* HOSTING DESTACADO */}
      <section>
        <AnimateIn>
          <div className="max-w-4xl mx-auto rounded-3xl p-8 md:p-12 bg-card border-2 border-border">
            <div className="flex items-start gap-5">
              <div className="w-14 h-14 rounded-2xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
                <Server className="w-7 h-7" />
              </div>
              <div>
                <h3 className="text-2xl md:text-3xl font-bold mb-3">Hosting y dominio incluidos el primer año</h3>
                <p className="text-muted-foreground">
                  Tu web incluye <strong className="text-foreground">hosting y dominio gratis durante el primer año</strong>. A partir del segundo año, la renovación es de solo <strong className="text-foreground">149€/año (IVA incluido)</strong> para mantener tu web online y tu dominio activo.
                </p>
              </div>
            </div>
          </div>
        </AnimateIn>
      </section>

      {/* 3 COLUMNAS QUICK INFO */}
      <section>
        <AnimateIn>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              { icon: <Package className="w-6 h-6" />, title: "Qué incluye", items: ["Hasta 8 páginas", "Textos + multilingüe", "Hosting + dominio 1 año"] },
              { icon: <Calendar className="w-6 h-6" />, title: "Plazo de entrega", items: ["Entre 20 y 40 días", "Depende del feedback y validaciones del cliente"] },
              { icon: <Tag className="w-6 h-6" />, title: "Inversión", items: ["1.500€ (IVA incluido)", "Pago único o 3 plazos con Klarna", "Renovación: 149€/año (hosting + dominio)"] },
            ].map((c, i) => (
              <Card key={i} className="p-6 hover:border-primary/30 hover:shadow-lg transition-all">
                <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-4">{c.icon}</div>
                <h4 className="font-semibold mb-3">{c.title}</h4>
                <ul className="space-y-2">
                  {c.items.map((it) => (
                    <li key={it} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <CheckCircle2 size={14} className="text-primary shrink-0 mt-0.5" />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            ))}
          </div>
        </AnimateIn>
      </section>

      {/* QUÉ NECESITAMOS */}
      <section>
        <AnimateIn>
          <div className="text-center mb-10 max-w-2xl mx-auto">
            <Badge variant="secondary" className="mb-4">Antes de empezar</Badge>
            <h3 className="text-3xl md:text-4xl font-bold mb-3">Qué necesitamos de ti</h3>
            <p className="text-muted-foreground">Para arrancar el proyecto y cumplir el plazo de entrega, necesitamos que nos facilites:</p>
          </div>
        </AnimateIn>
        <div className="max-w-3xl mx-auto space-y-3">
          {needsList.map((item, i) => (
            <AnimateIn key={i} delay={i * 0.04}>
              <div className="flex items-start gap-3 p-4 rounded-xl bg-card border border-border hover:border-primary/30 transition-colors">
                <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                  <FileText size={16} />
                </div>
                <span className="text-sm pt-1">{item}</span>
              </div>
            </AnimateIn>
          ))}
          <AnimateIn>
            <div className="flex items-start gap-3 p-4 rounded-xl bg-primary/5 border border-primary/20 mt-4">
              <Info size={18} className="text-primary shrink-0 mt-0.5" />
              <p className="text-sm text-muted-foreground">
                El plazo de entrega (<strong className="text-foreground">20-40 días</strong>) depende del feedback y los tiempos de validación del cliente. Empieza a contar cuando recibimos todo el material.
              </p>
            </div>
          </AnimateIn>
        </div>
      </section>

      {/* CÓMO TRABAJAMOS */}
      <section>
        <AnimateIn>
          <div className="text-center mb-10 max-w-2xl mx-auto">
            <Badge variant="secondary" className="mb-4">Proceso</Badge>
            <h3 className="text-3xl md:text-4xl font-bold mb-3">Cómo trabajamos</h3>
          </div>
        </AnimateIn>
        <div className="max-w-3xl mx-auto space-y-4">
          {steps.map((s, i) => (
            <AnimateIn key={s.n} delay={i * 0.06}>
              <div className="flex items-start gap-5 p-5 rounded-2xl bg-card border border-border hover:border-primary/30 transition-colors">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-accent text-primary-foreground flex items-center justify-center font-bold text-lg shrink-0">
                  {s.n}
                </div>
                <div>
                  <h4 className="font-semibold mb-1">{s.title}</h4>
                  <p className="text-sm text-muted-foreground">{s.desc}</p>
                </div>
              </div>
            </AnimateIn>
          ))}
        </div>
      </section>

      {/* EJEMPLOS */}
      <section>
        <AnimateIn>
          <div className="text-center mb-10 max-w-2xl mx-auto">
            <Badge variant="secondary" className="mb-4">Portfolio</Badge>
            <h3 className="text-3xl md:text-4xl font-bold mb-3">Ejemplos de webs que hemos hecho</h3>
            <p className="text-muted-foreground">Una muestra de webs corporativas que hemos diseñado para nuestros clientes.</p>
          </div>
        </AnimateIn>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            { name: "Pripolsa", url: "https://pripolsa.es", img: "/assets/screenshots/pripolsa.webp" },
            { name: "Grupo Jarpiscom", url: "https://grupojarpiscom.com", img: "/assets/screenshots/grupojarpiscom.webp" },
            { name: "Clinical Trainers", url: "https://clinicaltrainers.es", img: "/assets/screenshots/clinicaltrainers.webp" },
            { name: "Darkland", url: "https://darkland.cat", img: "/assets/screenshots/darkland.webp" },
            { name: "Canal Kevin", url: "https://canalkevin.es", img: "/assets/screenshots/canalkevin.webp" },
            { name: "APM Energia", url: "https://apmenergia.com", img: "/assets/screenshots/apmenergia.webp" },
          ].map((site, idx) => (
            <AnimateIn key={site.url} delay={idx * 0.06}>
              <a href={site.url} target="_blank" rel="noopener noreferrer" className="block group">
                <Card className="overflow-hidden hover:border-primary/30 hover:shadow-lg transition-all">
                  <div className="bg-muted border-b border-border">
                    <div className="h-7 flex items-center px-3 gap-1.5">
                      <div className="w-2.5 h-2.5 rounded-full bg-destructive/60" />
                      <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/60" />
                      <div className="w-2.5 h-2.5 rounded-full bg-green-500/60" />
                    </div>
                  </div>
                  <div className="aspect-video overflow-hidden">
                    <img
                      src={site.img}
                      alt={`Web corporativa de ${site.name}`}
                      className="w-full h-full object-cover object-top group-hover:scale-[1.02] transition-transform"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-4 flex items-center justify-between">
                    <span className="font-medium text-sm">{site.name}</span>
                    <ExternalLink size={14} className="text-muted-foreground group-hover:text-primary transition-colors" />
                  </div>
                </Card>
              </a>
            </AnimateIn>
          ))}
        </div>
      </section>

      {/* PRODUCT CARD */}
      <ProductCard
        title="Web Corporativa"
        description="Web profesional de hasta 8 páginas, multilingüe, con textos, hosting y dominio incluidos. Entrega en 20-40 días."
        price="1.500€"
        features={[
          "Hasta 8 páginas a medida",
          "Redacción profesional de textos",
          "Multilingüe sin máximo de idiomas",
          "SEO básico + Google Analytics 4",
          "Hosting + dominio incluidos el primer año",
          "Financiable en 3 plazos con Klarna",
        ]}
        stripeUrl="https://buy.stripe.com/9B64gzaH48r7bPJ9RA0480r"
      />

      {/* CTA FINAL */}
      <section>
        <AnimateIn>
          <div className="max-w-4xl mx-auto text-center rounded-3xl p-10 md:p-16 bg-gradient-to-br from-primary/10 via-accent/5 to-primary/10 border-2 border-primary/30 shadow-glow">
            <h3 className="text-3xl md:text-5xl font-bold mb-4">¿Listo para tener una web a la altura de tu marca?</h3>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              Pago único de 1.500€, financiable en 3 plazos con Klarna. Hosting y dominio del primer año incluidos.
            </p>
            <a href="/#contact" className="btn-pill bg-primary text-primary-foreground hover:shadow-glow px-8 py-4 inline-flex items-center gap-2">
              Solicitar mi web
              <ArrowRight size={16} />
            </a>
          </div>
        </AnimateIn>
      </section>
    </div>
  );
};

export default WebCorporativaDemo;
